import React, { useEffect, useState } from 'react';
import {
  Box,
  HeadingText,
  GDSStyleObject,
  FormField,
  TextInput,
  StackLayout,
  UtilityText,
} from '@leagueplatform/genesis-core';
import {
  formatPhoneNumber,
  isEmpty,
  isNotEmpty,
  isValidatePhoneLength,
  updateFormData,
  isNoSpace,
} from '../../../helpers';

export function PrescriberInformation(props: any) {
  const intialValue = {
    prescriberName: '',
    prescriberPhoneNumber: '',
  };
  const { errorHandling, nextButtonClicked, footerToBottom } = props;
  const [phNumError, setPhNumError] = useState<string>('');
  const [form, setForm] = useState(intialValue);
  const [errors, setErrors] = useState({
    prescriberName: false,
    prescriberPhoneNumber: false,
  });

  const formField =
    isEmpty(form.prescriberName) ||
    isEmpty(form.prescriberPhoneNumber) ||
    isValidatePhoneLength(form.prescriberPhoneNumber);

  const isValid =
    isNotEmpty(form.prescriberName) &&
    isNotEmpty(form.prescriberPhoneNumber) &&
    !isValidatePhoneLength(form.prescriberPhoneNumber);

  useEffect(() => {
    footerToBottom(false);
  }, [footerToBottom]);

  useEffect(() => {
    if (formField) {
      errorHandling(false);
      if (isEmpty(form.prescriberPhoneNumber)) {
        setPhNumError("Your prescriber's phone number is required.");
      } else if (isValidatePhoneLength(form.prescriberPhoneNumber)) {
        setPhNumError('Enter a valid phone number.');
      } else {
        setPhNumError('');
      }
    } else {
      errorHandling(true);
    }
  }, [formField, errorHandling, form]);

  useEffect(() => {
    if (nextButtonClicked) {
      if (!isValid) {
        setErrors({
          prescriberName: isEmpty(form.prescriberName),
          prescriberPhoneNumber:
            isEmpty(form.prescriberPhoneNumber) ||
            isValidatePhoneLength(form.prescriberPhoneNumber),
        });
      }
    }
  }, [nextButtonClicked, form, isValid]);

  const updateErrorState = (value: string, id: string) => {
    if (id === 'prescriberPhoneNumber') {
      if (isValidatePhoneLength(value)) {
        setPhNumError('Enter a valid phone number.');
        setErrors((prevState) => ({
          ...prevState,
          prescriberPhoneNumber: true,
        }));
      } else if (isEmpty(value)) {
        setPhNumError("Your prescriber's phone number is required.");
        setErrors((prevState) => ({
          ...prevState,
          prescriberPhoneNumber: true,
        }));
      } else {
        setPhNumError('');
      }
    } else if (id === 'prescriberName') {
      setErrors((prevState) => ({
        ...prevState,
        prescriberName: isEmpty(value),
      }));
    }
  };

  const onUpdateField = (event: any) => {
    const { value, id } = event.target;
    if (id === 'prescriberName') {
      setForm((prevState) => ({
        ...prevState,
        prescriberName: isNoSpace(value)
          ? ''
          : value?.replace(/[^a-zA-Z0-9 \-']+/gi, ''),
      }));
    }

    if (id === 'prescriberPhoneNumber') {
      setForm((prevState) => ({
        ...prevState,
        prescriberPhoneNumber: isNoSpace(value) ? '' : formatPhoneNumber(value),
      }));
    }
    updateErrorState(value, id);
  };

  useEffect(() => {
    const prescriberInfo = JSON.parse(
      sessionStorage.getItem('formData') || '{}',
    );
    if (prescriberInfo) {
      setForm({
        prescriberName: prescriberInfo?.prescriberName,
        prescriberPhoneNumber: prescriberInfo?.prescriberPhoneNumber,
      });
    }
  }, []);

  useEffect(() => {
    if (form) {
      updateFormData(form);
    }
  }, [form]);

  const onBlurField = (event: any) => {
    updateErrorState(event.target.value, event.target.id);
  };

  const onKeyPressField = (event: any) => {
    updateErrorState(event.target.value, event.target.id);
  };

  return (
    <StackLayout
      data-testid="presciber-information"
      horizontalAlignment="center"
      css={
        {
          backgroundColor: 'White',
          maxWidth: '800px',
        } as GDSStyleObject
      }
    >
      <Box
        css={
          {
            display: 'flex',
            flexDirection: 'column',
            padding: '$three $four',
          } as GDSStyleObject
        }
      >
        <HeadingText
          level="1"
          size={{
            '@initial': 'xxl',
            '@mobile': 'xl',
            '@mobileLandscape': 'xl',
            '@tablet': 'xl',
            '@laptop': 'xxl',
            '@desktop': 'xxl',
          }}
        >
          Step Therapy Exception Request Form
        </HeadingText>
        <Box
          css={
            {
              marginTop: '$two',
              marginBottom: '$one',
            } as GDSStyleObject
          }
        >
          <HeadingText
            level="3"
            size={{
              '@initial': 'xl',
              '@mobile': 'lg',
              '@mobileLandscape': 'lg',
              '@tablet': 'xl',
              '@laptop': 'xl',
              '@desktop': 'xl',
            }}
          >
            Step 2: Prescriber Information
          </HeadingText>
        </Box>
        <Box
          css={
            {
              display: 'flex',
            } as GDSStyleObject
          }
        >
          <UtilityText>Enter your doctor{`'`}s information below.</UtilityText>
        </Box>
        <Box>
          <Box
            css={
              {
                padding: '$one $none $none $one',
              } as GDSStyleObject
            }
          >
            <FormField
              id="prescriberName"
              label="Prescriber Name"
              statusIconLabel="Error"
              inputStatus={errors.prescriberName ? 'error' : undefined}
              statusMessage={
                errors.prescriberName
                  ? "Your prescriber's name is required."
                  : ''
              }
            >
              <TextInput
                name="prescriberName"
                value={form.prescriberName}
                onChange={onUpdateField}
                onBlur={onBlurField}
                onKeyPress={onKeyPressField}
                maxLength={50}
              />
            </FormField>
          </Box>
          <Box
            css={
              {
                padding: '$one $none $none $one',
              } as GDSStyleObject
            }
          >
            <FormField
              id="prescriberPhoneNumber"
              label="Phone Number"
              statusIconLabel="Error"
              inputStatus={errors.prescriberPhoneNumber ? 'error' : undefined}
              statusMessage={errors.prescriberPhoneNumber ? phNumError : ''}
            >
              <TextInput
                name="prescriberPhoneNumber"
                value={form.prescriberPhoneNumber}
                onChange={onUpdateField}
                onBlur={onBlurField}
                onKeyPress={onKeyPressField}
                maxLength={14}
              />
            </FormField>
          </Box>
        </Box>
      </Box>
    </StackLayout>
  );
}
